import React, { useEffect, Suspense, useState } from 'react'
import { Layout, ConfigProvider } from 'antd'
import { useQuery } from '@apollo/client'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom'
import { pathOr } from 'ramda'
import throttle from 'lodash.throttle'
import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { useLDClient, useFlags } from 'launchdarkly-react-client-sdk'
import HipaaModal from 'pages/HipaaModal'
import Sso from 'pages/Sso'
import CarePlanBuilder from 'pages/CarePlanBuilder/CarePlanBuilder'
import InactivityModal from 'components/InactivityModal'
import FullScreenLoader from 'components/FullScreenLoader'
import ProtectedRoute from 'components/ProtectedRoute'
import { signOut, refreshSession } from 'apollo/auth'
import CURRENT_USER from 'apollo/queries/CURRENT_USER'
import { timeDiff, isUserActive } from 'apollo/variables'
import { HIPAA, minute } from 'constants/constants'
import { SIDEBAR_WIDTH } from 'constants/styles'
import config from 'constants/config'
import {
  Users,
  TeamOrganizationGroups,
  Permissions,
  TeamContent
} from 'pages/Team'
import OrganizationGroup from 'pages/OrganizationGroup'

import {
  getFromStorage,
  setToStorage,
  removeFromStorage
} from 'utils/localStorage'
import {
  getFromSessionStorage,
  removeFromSessionStorage
} from 'utils/sessionStorage'

import NotificationsProvider from 'context/Notifications/NotificationsProvider'
import CleatTriageProvider from 'context/CleatTriage/CleatTriageProvider'
import TicketTypesProvider from 'context/TicketTypes/TicketTypesProvider'
import { GetNextProvider } from 'context/GetNext/GetNextProvider'
import {
  PermissionsProvider,
  SUBJECTS,
  collectTeamsPermissions
} from 'context/Permissions'

import useUserStore from 'stores/user'
import { useWebSocket } from '@rightway/websocket-multiplexer'

import {
  ROOT,
  SIGN_IN,
  SSO,
  BUILD_CARE_PLAN_ID_PATH,
  BUILD_CARE_PLAN_PATH,
  CARE_PLANS_PATH,
  CARE_PLAN_PATH,
  PROVIDERS_PATH,
  HIPAA_PATH,
  BENEFITS_CMS_PATH,
  TASKS_PATH,
  TICKETS_PATH,
  SUBMIT_CARE_PLAN_ID_PATH,
  NIC_REDIRECT_PATH,
  ORGANIZATION_GROUPS_PATH,
  THIRD_PARTY_ADMINS_PATH,
  THIRD_PARTY_ADMIN_PATH,
  INTAKE_GROUPS_PATH,
  INTAKE_GROUP_PATH,
  QUEUES_PATH,
  QUEUE_PATH,
  TEAMS_PATH,
  LIBRARY_PATH,
  LIBRARY_ARTICLE_PATH,
  LIBRARY_ARTICLE_CREATE_PATH,
  LIBRARY_ARTICLE_EDIT_PATH,
  PHARMACY_PLAN_PATH,
  PHARMACY_PLANS_PATH,
  PERMISSION_AUDIT_LOGS_PATH,
  QUEUE_SYSTEM_AUDIT_LOGS_PATH,
  QUEUE_SYSTEM_SETTINGS_PATH,
  ADVOCATE_PROFILES_PATH,
  ADVOCATE_PROFILE_EXTERNAL_PROFILE_PATH,
  ADVOCATE_PROFILE_MY_PROFILE_PATH,
  INVITES_PATH,
  TICKET_SETTINGS_PATH,
  ARTICLE_CATEGORIES_PATH,
  BENEFIT_CMS_SETTINGS_PATH,
  CARE_PODS_PATH,
  CARE_POD_DETAILS_PATH
} from 'constants/routes'
import { getTimeElapsedInHours } from 'utils/helpers/dateHelper'
import {
  interTab,
  onMessage,
  initActiveTab,
  postActiveTabMessage,
  shutdownTab
} from 'utils/activeTabHelper'
import LeftNav from 'components/LeftNav'
import NICWindow from 'pages/NIC/NICWindow'
import { getToken } from 'apollo/client'
import CarePodsLoader from './CarePodsLoader'
import { antdTheme } from './theme'

import { StyledRow, StyledContent, StyledLayout, StyledSider } from './styled'

/* Bill Advocacy */
const BillAdvocacy = React.lazy(() =>
  import('pages/BillAdvocacy' /* webpackChunkName:"bill_advocacy" */)
)
const Claim = React.lazy(() =>
  import('pages/Claim' /* webpackChunkName:"bill_advocacy" */)
)

/* Tickets */
const Ticket = React.lazy(() =>
  import('pages/Ticket' /* webpackChunkName:"ticket" */)
)

const Tickets = React.lazy(() => import('pages/Tickets'))

/* Organizations */
const Organization = React.lazy(() =>
  import('pages/Organization' /* webpackChunkName:"organizations" */)
)
const Organizations = React.lazy(() =>
  import('pages/Organizations' /* webpackChunkName:"organizations" */)
)

/* Third party admins */
const ThirdPartyAdmin = React.lazy(() => import('pages/ThirdPartyAdmin'))
const ThirdPartyAdmins = React.lazy(() => import('pages/ThirdPartyAdmins'))

const OrganizationGroups = React.lazy(() =>
  import('pages/OrganizationGroups' /* webpackChunkName:"organizationGroups" */)
)

/* Chats */
const Chats = React.lazy(() =>
  import('pages/Chats' /* webpackChunkName:"chats" */)
)

/* Chat */
const Chat = React.lazy(() =>
  import('pages/Chat' /* webpackChunkName:"chats" */)
)

/* Providers */
const Providers = React.lazy(() =>
  import('pages/Providers' /* webpackChunkName:"providers" */)
)
const Provider = React.lazy(() =>
  import('pages/Provider' /* webpackChunkName:"providers" */)
)

/* Faciclities */
const Facilities = React.lazy(() =>
  import('pages/Facilities' /* webpackChunkName:"facilities" */)
)
const Facility = React.lazy(() =>
  import('pages/Facility' /* webpackChunkName:"facilities" */)
)

/* Workaround for Benefits CMS */
const BenefitsCms = React.lazy(() =>
  import('pages/BenefitsCms' /* webpackChunkName:"benefits" */)
)
const PharmacyPlan = React.lazy(() =>
  import('pages/PharmacyPlan' /* webpackChunkName:"benefits" */)
)

const PharmacyPlans = React.lazy(() =>
  import('pages/PharmacyPlans' /* webpackChunkName:"benefits" */)
)

const BenefitsList = React.lazy(() =>
  import('pages/BenefitsList' /* webpackChunkName:"benefits" */)
)

const Library = React.lazy(() =>
  import('pages/Library' /* webpackChunkName:"library" */)
)

const ArticleView = React.lazy(() =>
  import(
    'pages/Library/components/ArticleView' /* webpackChunkName:"library" */
  )
)

const ArticleFormPage = React.lazy(() =>
  import(
    'pages/Library/components/ArticleFormPage' /* webpackChunkName:"library" */
  )
)

/* Roadmaps */
const Roadmaps = React.lazy(() =>
  import('pages/Roadmaps' /* webpackChunkName:"roadmaps" */)
)
const Roadmap = React.lazy(() =>
  import('pages/Roadmap' /* webpackChunkName:"roadmaps" */)
)
const CarePlanDraft = React.lazy(() =>
  import('pages/CarePlanDraft' /* webpackChunkName:"roadmaps" */)
)

/* Tasks */
const Tasks = React.lazy(() =>
  import('pages/Tasks' /* webpackChunkName:"tasks" */)
)

/* Queues */
const QueueSystemAuditLogs = React.lazy(() =>
  import(
    'pages/QueueSystemAuditLogs' /* webpackChunkName:"queue_system_audit_logs" */
  )
)

const Queue = React.lazy(() =>
  import('pages/Queue' /* webpackChunkName:"queues" */)
)

const Queues = React.lazy(() =>
  import('pages/Queues' /* webpackChunkName:"queues" */)
)

const IntakeGroups = React.lazy(() =>
  import(
    'pages/IntakeGroups/IntakeGroups' /* webpackChunkName:"intake_groups" */
  )
)

const IntakeGroup = React.lazy(() =>
  import('pages/IntakeGroup/IntakeGroup' /* webpackChunkName:"intake_groups" */)
)

const QueueSystemSettings = React.lazy(() =>
  import(
    'pages/QueueSystemSettings/QueueSystemSettings' /* webpackChunkName:"queue_system_settings" */
  )
)

const Tags = React.lazy(() =>
  import('pages/Tags' /* webpackChunkName:"tags" */)
)

const Invites = React.lazy(() => import('pages/Invites'))

const Teams = React.lazy(() =>
  import('pages/Teams' /* webpackChunkName:"teams" */)
)

const ArticleCategories = React.lazy(() => import('pages/ArticleCategories'))

const AdvocateProfiles = React.lazy(() =>
  import('pages/AdvocateProfiles' /* webpackChunkName:"advocate-profiles" */)
)

const ExternalProfile = React.lazy(() =>
  import(
    'pages/AdvocateProfileDetails/ExternalProfile' /* webpackChunkName:"advocate-profiles" */
  )
)

const MyProfile = React.lazy(() =>
  import(
    'pages/AdvocateProfileDetails/MyProfile' /* webpackChunkName:"advocate-profiles" */
  )
)

const PermissionAuditLogs = React.lazy(() =>
  import(
    'pages/PermissionAuditLogs' /* webpackChunkName:"permission_audit_logs" */
  )
)

/* Members */
const Members = React.lazy(() =>
  import('pages/Members' /* webpackChunkName:"members" */)
)
const Member = React.lazy(() =>
  import('pages/Member' /* webpackChunkName:"members" */)
)
const Medical = React.lazy(() =>
  import('pages/Member/Tabs/Medical/Medical' /* webpackChunkName:"members" */)
)

const NICRedirect = React.lazy(() => import('pages/NIC/NICRedirect'))

const TicketSettings = React.lazy(() =>
  import('pages/TicketSettings/TicketSettings')
)

const BenefitCmsSettings = React.lazy(() => import('pages/BenefitCmsSettings'))

const CarePods = React.lazy(() => import('pages/CarePods'))
const CarePodDetails = React.lazy(() => import('pages/CarePodDetails'))

const { BEFORE_REFRESH } = config

const events = [
  'mousemove',
  'mousedown',
  'wheel',
  'keydown',
  'touchstart',
  'touchmove'
]

const TABID = window.crypto.randomUUID()

const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed'

const AppRouter = React.memo(() => {
  const [collapsed, setCollapsed] = useState(
    () => getFromStorage(SIDEBAR_COLLAPSED_KEY)() === true
  )
  const handleToggle = () => {
    setCollapsed(prev => {
      const next = !prev
      setToStorage(SIDEBAR_COLLAPSED_KEY)(next)
      return next
    })
  }

  return (
    <StyledLayout>
      <StyledSider
        width={SIDEBAR_WIDTH}
        trigger={null}
        collapsible
        collapsed={collapsed}
      >
        <LeftNav collapsed={collapsed} onToggle={handleToggle} />
      </StyledSider>
      <StyledContent>
        <Suspense fallback={<FullScreenLoader />}>
          <Routes>
            <Route path={SIGN_IN} element={<Navigate to={ROOT} />} />
            <Route path={ROOT} element={<Navigate to={TICKETS_PATH} />} />
            <Route path="/*" element={<Navigate to={TICKETS_PATH} />} />

            <Route path={CARE_PLAN_PATH} element={<Roadmap />} />
            <Route path={CARE_PLANS_PATH} element={<Roadmaps />} />
            <Route path={BUILD_CARE_PLAN_PATH} element={<CarePlanBuilder />} />
            <Route
              path={BUILD_CARE_PLAN_ID_PATH}
              element={<CarePlanBuilder />}
            />
            <Route
              path={SUBMIT_CARE_PLAN_ID_PATH}
              element={<CarePlanDraft />}
            />
            <Route path="/chats" element={<Chats />} />
            <Route path="/chat/:id" element={<Chat />} />
            <Route path="/medical/:id" element={<Medical />} />
            <Route path="/members/:id" element={<Member />} />
            <Route path="/members/" element={<Members />} />
            <Route
              path="/bill-advocacy"
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.ClAIMS}>
                  <BillAdvocacy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bill-advocacy/claims/:id"
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.ClAIMS}>
                  <Claim />
                </ProtectedRoute>
              }
            />
            <Route
              path="/facilities"
              element={<Facilities isStandalonePage />}
            />
            <Route path="/facilities/:id" element={<Facility />} />
            <Route path="/doctors" element={<Navigate to={PROVIDERS_PATH} />} />
            <Route
              path="/doctors/:id"
              element={<Navigate to={`${PROVIDERS_PATH}/:id`} />}
            />
            <Route
              path={PROVIDERS_PATH}
              element={<Providers isStandalonePage />}
            />
            <Route path={`${PROVIDERS_PATH}/:id`} element={<Provider />} />

            <Route path={BENEFITS_CMS_PATH} element={<BenefitsList />} />
            <Route
              path={`${BENEFITS_CMS_PATH}/:id`}
              element={<BenefitsCms />}
            />
            <Route
              path={PHARMACY_PLAN_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.PHARMACY_PLANS}>
                  <PharmacyPlan />
                </ProtectedRoute>
              }
            />
            <Route
              path={PHARMACY_PLANS_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.PHARMACY_PLANS}>
                  <PharmacyPlans />
                </ProtectedRoute>
              }
            />
            <Route
              path={LIBRARY_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.LIBRARY}>
                  <Library />
                </ProtectedRoute>
              }
            />
            <Route
              path={LIBRARY_ARTICLE_CREATE_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.LIBRARY}>
                  <ArticleFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={LIBRARY_ARTICLE_EDIT_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.LIBRARY}>
                  <ArticleFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={LIBRARY_ARTICLE_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.LIBRARY}>
                  <ArticleView />
                </ProtectedRoute>
              }
            />

            <Route path="/organizations/:id" element={<Organization />} />
            <Route path="/organizations" element={<Organizations />} />
            <Route
              path={QUEUE_SYSTEM_AUDIT_LOGS_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.INTAKE_QUEUE}>
                  <QueueSystemAuditLogs />
                </ProtectedRoute>
              }
            />

            <Route
              path={QUEUES_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.INTAKE_QUEUE}>
                  <Queues />
                </ProtectedRoute>
              }
            />
            <Route
              path={INTAKE_GROUPS_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.INTAKE_GROUP}>
                  <IntakeGroups />
                </ProtectedRoute>
              }
            />
            <Route
              path={INTAKE_GROUP_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.INTAKE_GROUP}>
                  <IntakeGroup />
                </ProtectedRoute>
              }
            />
            <Route
              path={QUEUE_SYSTEM_SETTINGS_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.INTAKE_QUEUE}>
                  <QueueSystemSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path={ORGANIZATION_GROUPS_PATH}
              element={
                <ProtectedRoute
                  permissionSubject={SUBJECTS.ORGANIZATION_GROUPS}
                >
                  <OrganizationGroups />
                </ProtectedRoute>
              }
            />
            <Route
              path={`${ORGANIZATION_GROUPS_PATH}/:id/organizations`}
              element={
                <ProtectedRoute
                  permissionSubject={SUBJECTS.ORGANIZATION_GROUPS}
                >
                  <OrganizationGroup />
                </ProtectedRoute>
              }
            />
            <Route
              path={THIRD_PARTY_ADMIN_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.ADMIN}>
                  <ThirdPartyAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path={THIRD_PARTY_ADMINS_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.ADMIN}>
                  <ThirdPartyAdmins />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tags"
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.INTAKE_TAG}>
                  <Tags />
                </ProtectedRoute>
              }
            />
            <Route
              path={INVITES_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.INVITES}>
                  <Invites />
                </ProtectedRoute>
              }
            />
            <Route
              path={TEAMS_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.TEAMS}>
                  <Teams />
                </ProtectedRoute>
              }
            />
            <Route
              path={ARTICLE_CATEGORIES_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.ADMIN}>
                  <ArticleCategories />
                </ProtectedRoute>
              }
            />
            <Route
              path={ADVOCATE_PROFILES_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.USERS}>
                  <AdvocateProfiles />
                </ProtectedRoute>
              }
            />
            <Route
              path={ADVOCATE_PROFILE_EXTERNAL_PROFILE_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.USERS}>
                  <ExternalProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path={ADVOCATE_PROFILE_MY_PROFILE_PATH}
              element={<MyProfile />}
            />
            <Route
              path={`${TEAMS_PATH}/:id/users`}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.TEAMS}>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path={`${TEAMS_PATH}/:id/organization-groups`}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.TEAMS}>
                  <TeamOrganizationGroups />
                </ProtectedRoute>
              }
            />
            <Route
              path={`${TEAMS_PATH}/:id/permissions`}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.TEAMS}>
                  <Permissions />
                </ProtectedRoute>
              }
            />
            <Route
              path={PERMISSION_AUDIT_LOGS_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.TEAMS}>
                  <PermissionAuditLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path={`${TEAMS_PATH}/:id/content`}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.TEAMS}>
                  <TeamContent />
                </ProtectedRoute>
              }
            />
            <Route
              path={QUEUE_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.INTAKE_QUEUE}>
                  <Queue />
                </ProtectedRoute>
              }
            />
            <Route
              path={TICKET_SETTINGS_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.TICKET_SETTINGS}>
                  <TicketSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path={BENEFIT_CMS_SETTINGS_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.ADMIN}>
                  <BenefitCmsSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path={CARE_PODS_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.ADMIN}>
                  <CarePods />
                </ProtectedRoute>
              }
            />
            <Route
              path={CARE_POD_DETAILS_PATH}
              element={
                <ProtectedRoute permissionSubject={SUBJECTS.ADMIN}>
                  <CarePodDetails />
                </ProtectedRoute>
              }
            />
            <Route path={TASKS_PATH} element={<Tasks />} />
            <Route path="/ticket/:id" element={<Ticket />} />
            <Route path={TICKETS_PATH} element={<Tickets />} />
            <Route path={HIPAA_PATH} element={<HipaaModal visible />} />
            <Route path={NIC_REDIRECT_PATH} element={<NICRedirect />} />
            <Route from={SSO} element={<Sso />} />
          </Routes>
        </Suspense>
      </StyledContent>
      {/* Rendered outside <Routes> so the call stays audible across navigation */}
      <NICWindow />
      <InactivityModal />
    </StyledLayout>
  )
})

const Main = () => {
  const location = useLocation()
  const setUserId = useUserStore(s => s.setUserId)
  const setUserName = useUserStore(s => s.setUserName)
  const setUserPermissions = useUserStore(s => s.setUserPermissions)
  const { reauthenticate } = useWebSocket()
  const { useRtgEvents } = useFlags()

  const { loading, error, data } = useQuery(CURRENT_USER)

  const ldClient = useLDClient()

  useEffect(() => {
    const userId = pathOr(null, ['current_user', 'id'], data)

    if (userId) {
      const userName = pathOr('', ['current_user', 'full_name'], data)
      const userEmail = pathOr('', ['current_user', 'email'], data)

      datadogRum.setUser({ id: userId, name: userName })
      datadogLogs.setUser({ id: userId, name: userName })

      ldClient?.identify({
        key: userId,
        name: userName,
        email: userEmail
      })

      setUserId(userId)
      setUserName(userName)

      const userTeams = pathOr([], ['current_user', 'teams'], data)
      const teamPermissions = collectTeamsPermissions(userTeams)

      setUserPermissions(teamPermissions)
    }
  }, [ldClient, data, setUserPermissions, setUserId, setUserName])

  useEffect(() => {
    if (!useRtgEvents) return

    const interval = setInterval(() => {
      const currentToken = getToken()
      reauthenticate(currentToken)
    }, 3 * minute)
    return () => clearInterval(interval)
  }, [reauthenticate, useRtgEvents])

  const pathname = pathOr(ROOT, ['pathname'], location)

  const hipaaAcceptedAt = getFromStorage(HIPAA)()
  // Bool condition for backward compatibility, can be removed after some months
  const hipaaRecentlyAccepted =
    hipaaAcceptedAt !== true && getTimeElapsedInHours(hipaaAcceptedAt) < 12
  const shouldPromptHipaa = !hipaaRecentlyAccepted && pathname !== HIPAA_PATH

  useEffect(() => {
    const setActivityToStorage = throttle(
      () => {
        const active = isUserActive()
        if (active) {
          setToStorage('lastUserActivityAt')(Date.now())
          postActiveTabMessage(TABID)
        }
      },
      10000,
      { trailing: false }
    )

    const doHandle = e => onMessage(e, TABID)
    interTab.addEventListener('message', doHandle)

    initActiveTab(TABID)
    setToStorage('lastUserActivityAt')(Date.now())
    events.forEach(item =>
      document.addEventListener(item, setActivityToStorage)
    )

    const timer = setInterval(() => {
      if (!getFromStorage('token')()) {
        clearInterval(timer)
        return
      }
      const diff = timeDiff()
      const tokenExpiresAtIso = getFromStorage('tokenExpiresAtIso')()
      const timeBeforeExpiring =
        (tokenExpiresAtIso * 1000 - Date.now() - diff) / minute
      if (timeBeforeExpiring < BEFORE_REFRESH) {
        refreshSession(false, true)
      }
    }, minute)

    return () => {
      clearInterval(timer)
      events.forEach(item =>
        document.removeEventListener(item, setActivityToStorage)
      )
      interTab.removeEventListener('message', doHandle)
    }
  }, [])

  useEffect(() => {
    const unloadCallback = () => {
      shutdownTab(TABID)
    }

    window.addEventListener('beforeunload', unloadCallback)
    return () => window.removeEventListener('beforeunload', unloadCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shutdownTab])

  const hasErrorNoData = error && !data

  useEffect(() => {
    if (!hasErrorNoData) return

    const timeout = setTimeout(() => {
      if (pathname !== ROOT) {
        setToStorage('originalLink')(pathname)
      }
      signOut()
    }, 5000)

    return () => clearTimeout(timeout)
  }, [hasErrorNoData, pathname])

  const navigate = useNavigate()
  const isInitialLoadFinished = !hasErrorNoData && (data || !loading)

  useEffect(() => {
    if (!isInitialLoadFinished) return

    if (shouldPromptHipaa) {
      navigate(HIPAA_PATH, { replace: true })
      return
    }

    const lastVisitedPage = getFromSessionStorage('lastVisitedPage')()
    if (lastVisitedPage) {
      removeFromSessionStorage('lastVisitedPage')
      navigate(lastVisitedPage, { replace: true })
      return
    }

    const originalLink = getFromStorage('originalLink')()
    if (originalLink && originalLink !== ROOT && pathname !== HIPAA_PATH) {
      removeFromStorage('originalLink')
      navigate(originalLink, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialLoadFinished])

  if (loading && !data) return <FullScreenLoader />

  if (hasErrorNoData) {
    return (
      <Layout>
        <StyledRow type="flex" align="middle" justify="center">
          <span>{error?.message}</span>
          <span>You will be redirected...</span>
        </StyledRow>
      </Layout>
    )
  }

  return (
    <ConfigProvider theme={antdTheme}>
      <PermissionsProvider>
        <CarePodsLoader />
        <NotificationsProvider tabId={TABID}>
          <CleatTriageProvider>
            <TicketTypesProvider>
              <GetNextProvider>
                <AppRouter />
              </GetNextProvider>
            </TicketTypesProvider>
          </CleatTriageProvider>
        </NotificationsProvider>
      </PermissionsProvider>
    </ConfigProvider>
  )
}

export default Main
